import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useAuthContext } from 'AuthProvider'
import {type CellValueChangedEvent } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { EditStatusCellRenderer, LoadingOverlay } from 'components/commonparts/index'
import { useGridEdit } from 'hooks/useGridEdit'
import { useTransportRelation } from 'hooks/useTransitionRelation'
import { initRecordEditEx, type DataListPropsBase, type RecordEditEx } from 'types/dataGridTypes'
import { validateRequired } from 'utils/validation'

import type { ColDef, ColGroupDef, IRichCellEditorParams, ValueFormatterParams, ValueParserParams } from 'ag-grid-enterprise'
import type { TransportRelation, GetMstTransportRelationTransportRelationGetRequest, MstTransportRelation, MstCustomer, MstCustomerCenter, MstCenter } from 'api-client'
import 'utils/mst.scss'

const columnMapping: Record<string, string> = {
  org_center_id: 'orgCenterId',
  org_customer_id: 'orgCustomerId',
  org_customer_center_id: 'orgCustomerCenterId',
  leadtime: 'leadtime',
  max_traffic_volume: 'maxTrafficVolume',
  remarks: 'remarks',
  delete_flg: 'deleteFlg',
}
const primayKey = ['orgCenterId', 'orgCustomerId', 'orgCustomerCenterId']

interface DataListProps extends DataListPropsBase {
  list: TransportRelation
  center: MstCenter[]
  customer: MstCustomer[]
  customerCenter: MstCustomerCenter[]
  searchParam: GetMstTransportRelationTransportRelationGetRequest
  gridRef?: React.RefObject<AgGridReact>
  findSearchValue?: string
  onFindChanged?: (event: any) => void
  isSearchVisible?: boolean
}

interface MstTransportRelationWithId extends MstTransportRelation, RecordEditEx {}

function DataList({
  list,
  center,
  customer,
  customerCenter,
  isLoading,
  searchParam,
  onReload,
  updateTrigger,
  onUploadData,
  setHasGridEdited,
  setResultMessage,
  setDownloadColumnDefs,
  gridRef: externalGridRef,
  findSearchValue: externalFindSearchValue,
  onFindChanged: externalOnFindChanged,
  isSearchVisible = false,
}: DataListProps) {
  const { loginUserInfo } = useAuthContext()
  const { transportRelationSearchResult, fetchTransportRelationUpdate, isUpdating, result } = useTransportRelation()
  const internalGridRef = useRef<AgGridReact<MstTransportRelationWithId>>(null)
  const gridRef = externalGridRef || internalGridRef
  
  const containerStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 40 : 0
    return { width: '100%', height: `calc(100vh - ${180 + searchBoxHeight}px)` }
  }, [isSearchVisible])
  
  const [editorDataReady, setEditorDataReady] = useState(false)
  const gridStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 40 : 0
    return { width: '100%', height: `calc(100vh - ${180 + searchBoxHeight}px)` }
  }, [isSearchVisible])
  const [row, setRow] = useState<MstTransportRelationWithId[]>(list.list.map((item) => ({ ...item, ...initRecordEditEx() })))
  const [centerSelect, setCenterSelect] = useState<(string | null)[]>([])
  const [customerSelect, setCustomerSelect] = useState<(string | null)[]>([])
  const [customerCenterSelect, setCustomerCenterSelect] = useState<(string | null)[]>([])
  const [rowChangeTriggerRowID, setRowChangeTriggerRowID] = useState<string | null>(null);

  useEffect(() => {
    if (customerSelect.length > 0 && customerCenterSelect.length > 0 && centerSelect.length > 0) {
      setEditorDataReady(true)
    }
  }, [customerSelect, customerCenterSelect, centerSelect])


  useEffect(() => {
    const values = customer?.map((r) => r.orgCustomerId) || []
    setCustomerSelect(values)
  }, [customer])

  useEffect(() => {
    const values = customerCenter?.map((r) => r.orgCustomerCenterId) || []
    setCustomerCenterSelect(values)
  }, [customerCenter])

  useEffect(() => {
    // console.log("items", items)
    // if (items.length === 0) return
    setEditorDataReady(false)
    const values = center?.map((r) => r.orgCenterId) || []
    setCenterSelect([...values])
  }, [center])

  useEffect(() => {
    setResultMessage(result)
  }, [result])

  useEffect(() => {
    setResultMessage(transportRelationSearchResult)
  }, [transportRelationSearchResult])

  const createBlankRow = useCallback((): MstTransportRelationWithId => {
    const searchCenterDefault = center.find((rowCenter) => rowCenter.orgCenterId === searchParam.orgCenterId)
    const blankRow: Partial<MstTransportRelationWithId> = {
      companyId: loginUserInfo?.companyId || null,
      orgCustomerId: searchParam?.orgCustomerId, 
      orgCustomerCenterId: null,
      orgCenterId: searchCenterDefault?.orgCenterId || null,
      maxTrafficVolume: null,
      leadtime: null,
      remarks: null,
      deleteFlg: false,
      insertDatetime: null,
      updateDatetime: null,
      insertUser: loginUserInfo?.userId || null,
      deleteDatetime: null,
      deleteUser: null,
      updateUser: null,
      customerCompanyId: null,
      customerCenterId: null,
      transportOrgCenterId: null,
      ...initRecordEditEx(true),
    }
    return blankRow as MstTransportRelationWithId
  }, [searchParam, loginUserInfo?.companyId, loginUserInfo?.userId])

  useEffect(() => {
    // 値変更時に、useStateの値を参照する処理を行う場合
    // まとめてコピペした場合など、1回しか発生しないので、変更行はまとめて設定する
    if (!rowChangeTriggerRowID) return
    // console.log("cellChangeTrigger", rowChangeTriggerRowID, items.length)
    const updatedRows: MstTransportRelationWithId[] = [];
    gridRef.current?.api?.forEachNode((node) => {
      if (node?.data) {
        const trRow = node.data as MstTransportRelationWithId
        // console.log("cellChangeTrigger",trRow.isEdited, `'${trRow.orgCenterId}'`,searchParam.orgItemCode)
        if (trRow.isEdited) {
          // 新規行にセンターIDが入っていない場合は、検索条件のセンターIDをセット
          if (!trRow.orgCenterId) {
            const searchCenterDefault = center.find((rowCenter) => rowCenter.orgCenterId === searchParam.orgCenterId)
            trRow.orgCenterId = searchCenterDefault?.orgCenterId || null
          }
          if (!trRow.orgCustomerId) {
            const searchCenterDefault = customer.find((rowCenter) => rowCenter.orgCustomerId === searchParam.orgCustomerId)
            trRow.orgCustomerId = searchCenterDefault?.orgCustomerId || null
          }
          // 商品マスタの値を設定
          // node.setData(trRow)
          updatedRows.push(trRow)
        }
      }
    })
    if (updatedRows.length > 0) gridRef.current?.api?.applyTransaction({ update: updatedRows })
    // 必要な処理が終わったら、NULLに戻すこと！
    setRowChangeTriggerRowID(null);
  }, [rowChangeTriggerRowID])

  // 入力チェック
  const fieldValidate = (pRow: MstTransportRelationWithId, field: string | undefined, newValue: any): boolean => {
    let hasErrors = false
    switch (field) {
      case 'orgCenterId':
      case 'orgCustomerId': 
      case 'orgCustomerCenterId':
      {
        hasErrors = checkAndSetError(pRow, field, validateRequired(newValue))
        if (!hasErrors) {
          hasErrors = duplicateCheck(pRow, newValue)
        }
        break
      }
      default:
        return false
    }
    // console.log('fieldValidate', field, pRow, hasErrors)
    return hasErrors
  }
  const cellValueChanged = useCallback((event: CellValueChangedEvent<MstTransportRelationWithId>, hasError: boolean) => {
    const { data, colDef, newValue, oldValue, node } = event
    const { field } = colDef
    // なぜか以下のconsole.logをコメントアウトすると、新規行にセンターID設定がうまく動かない・・・
    const updatedRow = { ...data }
    if (field === "orgCenterId") {
      // 仕入先コード変更時に、APIのエラーを解消
      checkAndSetError(updatedRow, "orgCenterId", null);
      checkAndSetError(updatedRow, "orgCustomerId", null);
      // API以外のエラーを再度チェック
      fieldValidate(updatedRow, "orgCenterId", updatedRow.orgCenterId)
      fieldValidate(updatedRow, "orgCustomerId", updatedRow.orgCustomerId)
    }
    if (field === 'orgCustomerId' && newValue !== oldValue) {
        node.setDataValue('orgCustomerCenterId', null)
        checkAndSetError(data, 'orgCustomerCenterId', null)
      }
  }, [list])

  const updateMstTranstionRelation = useCallback(
    async (updatedRows: MstTransportRelationWithId[]) => {
      const datalist = updatedRows.map(({ isEdited, editedFields, errors, ...rest }) => ({
        ...rest,
      }))

      try {
        await fetchTransportRelationUpdate({
          mstTransportRelationUpdate: { body: datalist },
        })
        if (onReload) onReload()
      } catch (err: any) {
        if (err?.errors && err.result?.status === 'error') {
          const gridApi = gridRef.current?.api

          err.errors.forEach((errrow: any) => {
            const { rowdata } = errrow
            const orgCenterId = (rowdata as { org_center_id: string | null })?.org_center_id
            const orgCustomerId = (rowdata as { org_customer_id: string | null })?.org_customer_id
            const orgCustomerCenterId = (rowdata as { org_customer_center_id: string | null })?.org_customer_center_id
            const errRow = updatedRows.find((rowdatas) => 
              rowdatas.orgCenterId === orgCenterId &&
              rowdatas.orgCustomerId === orgCustomerId &&
              rowdatas.orgCustomerCenterId === orgCustomerCenterId
            )
            if (errRow) {
              // UpdateAPIのErrorは、PKをエラー表記して、サーバーから取得したエラーを表示する
              errRow.errors = { 
                orgCenterId: [errrow.errorMessage],
                orgCustomerCenterId: [errrow.errorMessage],
                orgCustomerId: [errrow.errorMessage], 
              }
              const rowNode = gridApi?.getRowNode(errRow.id || '')
              if (rowNode) {
                rowNode.setData(errRow)
              }
            }
          })
        }
      }
    },
    [fetchTransportRelationUpdate, searchParam]
  )

  const handleImportExcel = (data: any[]) => {
    let hasError = false
    const mappedData = data.map((excelRow: any) => {
      const mappedRow: Partial<MstTransportRelationWithId> = {}
      Object.keys(columnMapping).forEach((key) => {
        if (key in excelRow) {
          const tableField = columnMapping[key as keyof typeof columnMapping]
          const importvalue = excelRow[key]
          let setvalue = importvalue || null

          if ([ 'deleteFlg'].includes(tableField)) {
            setvalue = parseBoolean(importvalue)
          } else if ([ 'leadtime', 'maxTrafficVolume'].includes(tableField)) {
            setvalue = parsePositiveIntegerZero(importvalue) as unknown as undefined;
          } else {
            setvalue = importvalue ? String(importvalue).trim() : null
          }
          // パース出来ないときはスキップ（＝NULL）し、警告メッセージを表示
          if (!isNonInputValue(importvalue) && setvalue === null) hasError = true
          mappedRow[tableField as keyof MstTransportRelationWithId] = setvalue as unknown as undefined
        }
      })
      return mappedRow as MstTransportRelationWithId
    })
    return { mappedData, hasError }
  }

  const {
    booleanFormatter,
    dateFormatter,
    excelStyles,
    selectValueFormatter,
    typecodeFormatter,
    formatBooleanValue,
    formatDecimalValue,
    isNonInputValue,
    numberValueFormatter,
    numberValueParser,
    numberValueParserZero,
    parseBoolean,
    parseBooleanValue,
    parseDate,
    parseDateValue,
    parseDecimalValue,
    parseNumeric,
    parsePositiveInteger,
    parsePositiveIntegerZero,
    parseStringValue,
    parseTime,
    selectTypecodeValueParser,
    selectValueParser,
    typeCodeParser,
    localeText,
    autoSizeStrategy,
    onGridReady,
    getCellClassRules,
    checkAndSetError,
    rowDataValidate,
    colDataValidate,
    duplicateCheck,
    isEnableEdit,
    longTextEditorParams,
    longTextValueFormatter,
    longTextValueParser,
    longTextValueSetter,
    editInfoValueGetter,
    clearPinnedRows,
  } = useGridEdit({
    gridRef,
    isLoading,
    isUpdating,
    onReload,
    onUploadData,
    updateTrigger,
    setHasGridEdited,
    setResultMessage,
    columnMapping,
    primayKey,
    updateData: updateMstTranstionRelation,
    handleImportExcel,
    createBlankRow,
    fieldValidate,
    cellValueChanged,
    setRowChangeTriggerRowID,
    storageKey: 'gridColumnState:/master-transport',
    scrollPositionKey: 'orgCenterId',
    rowData: row,
  })

  /**
   * カラム定義
   * @returns
   */
  const updateColumnDefs = (): (ColDef | ColGroupDef)[] => [
    {
      headerName: '!',
      rowDrag: true,
      cellRenderer: EditStatusCellRenderer,
      valueGetter: (params) => editInfoValueGetter(params),
      pinned: 'left',
      width: 100,
      filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          {
            filter: 'agSetColumnFilter',
            filterParams: {
              values: ['入力エラー', '変更', null],
              refreshValuesOnOpen: true,
              applyMiniFilterWhileTyping: true,
              excelMode: 'mac',
            },
          },
        ],
        buttons: ['reset'],
        closeOnApply: true,
        applyMiniFilterWhileTyping: true,
        excelMode: 'mac',
      },
    },
    {
      headerName: '発送元センターID',
      field: 'orgCenterId',
      editable: (params) => isEnableEdit(params.data && params.data.isNewRecord),
      cellClassRules: {
        ...getCellClassRules(),
        'editable-cell': (params) => isEnableEdit(params.data && params.data.insertDatetime === null),
      },
      pinned: 'left',
      width: 300,
      suppressPaste: false,
      cellEditor: 'agRichSelectCellEditor',
      valueFormatter: (params: ValueFormatterParams) =>
      selectValueFormatter(params.value, center, 'orgCenterId', 'centerName'),
      filterValueGetter: (params) => selectValueFormatter(params.data.orgCenterId, center, "orgCenterId", "centerName"),
      valueParser: (params: ValueParserParams) => selectValueParser(params, center, 'orgCenterId'),
      cellEditorParams: editorDataReady
        ? ({
            values: [null, ...centerSelect],
            formatValue: (value: string) => selectValueFormatter(value, center, 'orgCenterId', 'centerName'),
            searchType: 'matchAny',
            allowTyping: true,
            filterList: true,
            highlightMatch: true,
          } as IRichCellEditorParams)
        : { values: ['-'] },
    },
    {
      headerName: '得意先企業ID',
      field: 'orgCustomerId',
      editable: (params) => isEnableEdit(params.data && params.data.isNewRecord),
      cellClassRules: {
        ...getCellClassRules(),
        'editable-cell': (params) => isEnableEdit(params.data && params.data.insertDatetime === null),
      },
      pinned: 'left',
      width: 300,
      cellEditor: 'agRichSelectCellEditor',
      valueFormatter: (params: ValueFormatterParams) =>
        selectValueFormatter(params.value, customer, 'orgCustomerId', 'customerName'),
      filterValueGetter: (params) => selectValueFormatter(params.data.orgCustomerId, customer, "orgCustomerId", "customerName"),
      valueParser: (params: ValueParserParams) => selectValueParser(params, customer, 'orgCustomerId'),
      cellEditorParams: editorDataReady
        ? ({
            values: [null, ...customerSelect],
            formatValue: (value: string) => selectValueFormatter(value, customer, 'orgCustomerId', 'customerName'),
            searchType: 'matchAny',
            allowTyping: true,
            filterList: true,
            highlightMatch: true,
          } as IRichCellEditorParams)
        : { values: ['-'] },
    },
    {
      headerName: '発送先センターID',
      field: 'orgCustomerCenterId',
      editable: (params) => isEnableEdit(params.data && params.data.isNewRecord),
      cellClassRules: {
        ...getCellClassRules(),
        'editable-cell': (params) => isEnableEdit(params.data && params.data.insertDatetime === null),
      },
      pinned: 'left',
      width: 400,
      cellEditor: 'agRichSelectCellEditor',
      valueFormatter: (params: ValueFormatterParams) =>
        selectValueFormatter(params.value, customerCenter, 'orgCustomerCenterId', 'customerCenterName'),
      filterValueGetter: (params) => selectValueFormatter(params.data.orgCustomerCenterId, customerCenter, "orgCustomerCenterId", "customerCenterName"),
      valueParser: (params: ValueParserParams) => selectValueParser(params, customerCenter, 'orgCustomerCenterId'),
      // cellEditorParams: editorDataReady
      //   ? ({
      //       values: [null, ...customerCenterSelect],
      //       formatValue: (value: string) => selectValueFormatter(value, customerCenter, 'orgCustomerCenterId', 'customerCenterName'),
      //       searchType: 'matchAny',
      //       allowTyping: true,
      //       filterList: true,
      //       highlightMatch: true,
      //     } as IRichCellEditorParams)
      //   : { values: ['-'] },
      cellEditorParams: (params: any) => {
        const selectedCust = params.data?.orgCustomerId;
        const options = selectedCust
              ? customerCenter
              .filter(cc => cc.orgCustomerId === selectedCust)
              .map(cc => cc.orgCustomerCenterId)
          : [];
          return {
          values: [null, ...options],
          formatValue: (value: string) =>
          selectValueFormatter(value, customerCenter, 'orgCustomerCenterId', 'customerCenterName'),
          searchType: 'matchAny',
          allowTyping: true,
          filterList: true,
          highlightMatch: true,
       } as IRichCellEditorParams;
      },
    },
    {
      headerName: '納品リードタイム',
      field: 'leadtime',
      editable: isEnableEdit(),
      flex: 1,
      cellClass: 'number-cell',
      cellEditor:'agNumberCellEditor',
      valueFormatter: numberValueFormatter,
      valueParser: numberValueParserZero, 
    },
    {
      headerName: '一日の最大輸送量',
      field: 'maxTrafficVolume',
      editable: isEnableEdit(),
      flex: 1,
      cellClass: 'number-cell',
      filter: 'agNumberColumnFilter',
      cellEditor:'agNumberCellEditor',
      valueFormatter: numberValueFormatter,
      valueParser: numberValueParserZero, 
    },
    {
      headerName: '備考',
      field: 'remarks', 
      editable: isEnableEdit(),
      cellEditorPopup: true,
      cellEditor: 'agLargeTextCellEditor',
      cellEditorParams: longTextEditorParams,
      valueFormatter: longTextValueFormatter,
      valueParser: longTextValueParser,
      valueSetter: longTextValueSetter,
    },
    {
      headerName: '削除',
      field: 'deleteFlg',
      flex: 1,
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      valueFormatter: formatBooleanValue,
      valueParser: parseBooleanValue,
      editable: isEnableEdit(),
    },
    {
      headerName: '得意先PSI企業ID',
      field: 'customerCompanyId',
      flex: 1,
    },
    {
      headerName: 'PSI得意先センターID',
      field: 'customerCenterId',
      flex: 1,
    },
    {
      headerName: '移送先センターID',
      field: 'transportOrgCenterId',
      flex: 1,
    },
  ]

  const columnDefs = useMemo<ColDef[]>(updateColumnDefs, [center, editorDataReady])

  // columnDefsが再計算された際に、保存済みのカラム表示設定を再適用する
  useEffect(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) return
    try {
      const savedAll = localStorage.getItem('gridColumnStates')
      if (savedAll) {
        const obj = JSON.parse(savedAll) as Record<string, any>
        const state = obj?.['gridColumnState:/master-transport']
        if (Array.isArray(state) && state.length > 0) {
          // requestAnimationFrameでAG GridがcolumnDefsを処理した後に適用
          requestAnimationFrame(() => {
            if (!api.isDestroyed()) {
              api.applyColumnState({ state, applyOrder: true })
            }
          })
        }
      }
    } catch { /* noop */ }
  }, [columnDefs])

  // ダウンロードファイル用のカラム定義
  useEffect(() => {
    if (setDownloadColumnDefs)
      setDownloadColumnDefs([
        { headerName: 'PSI企業ID', children: [{ headerName: 'company_id', field: 'companyId' }] },
        { headerName: '発送元センターID', children: [{ headerName: 'org_center_id', field: 'orgCenterId', cellClass: 'stringType' }] },
        { headerName: '得意先企業ID', children: [{ headerName: 'org_customer_id', field: 'orgCustomerId', cellClass: 'stringType' }] },
        { headerName: '発送先センターID', children: [{ headerName: 'org_customer_center_id', field: 'orgCustomerCenterId', cellClass: 'stringType' }] },
        { headerName: '納品リードタイム', children: [{ headerName: 'leadtime', field: 'leadtime', cellClass: 'stringType' }] },
        { headerName: '一日の最大輸送量', children: [{ headerName: 'max_traffic_volume', field: 'maxTrafficVolume', cellClass: 'stringType' }] },
        { headerName: '備考', children: [{ headerName: 'remarks', field: 'remarks', cellClass: 'stringType' }] },
        { headerName: '削除', children: [{ headerName: 'delete_flg', field: 'deleteFlg', cellClass: 'booleanType' }] },
        { headerName: '得意先PSI企業ID', children: [{ headerName: 'customer_company_id', field: 'customerCompanyId', cellClass: 'stringType' }] },
        { headerName: 'PSI得意先センターID', children: [{ headerName: 'customer_center_id', field: 'customerCenterId', cellClass: 'stringType' }] },
        { headerName: '移送先センターID', children: [{ headerName: 'transport_org_center_id', field: 'transportOrgCenterId', cellClass: 'stringType' }] },
      ])
  }, [setDownloadColumnDefs])

  useEffect(() => {
    if (list.list.length === 0) {
      const blankRow = createBlankRow()
      setRow([blankRow])
    } else {
      setRow(list.list.map((item) => ({ ...item, ...initRecordEditEx() })))
      clearPinnedRows()
    }
  }, [list.list])

  return (
    <div style={{ ...containerStyle, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flexGrow: 1, boxSizing: 'border-box' }}>
        <div style={gridStyle} className="ag-theme-balham">
          <AgGridReact
            rowData={row}
            ref={gridRef}
            getRowId={(params) => params.data.id}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            localeText={localeText}
            autoSizeStrategy={autoSizeStrategy}
            loadingOverlayComponent={LoadingOverlay}
            findSearchValue={externalFindSearchValue}
            onFindChanged={externalOnFindChanged}
          />
        </div>
      </div>
    </div>
  )
}

export default DataList
